/**
 * 绝对路径: D:/XXY/MeiShan_Project/frontend/shared/utils/area.ts
 * 提供行政区划代码与名称互转等便捷函数。
 * 输入输出: 以完整路径代码数组 [省,市,区] 为主；返回名称数组或拼接字符串。
 */

import type { AreaOption } from '../data/china-area'
import { CHINA_AREA_OPTIONS } from '../data/china-area'

/**
 * 构建基础索引，提升查找效率
 * - codeToLabel: 代码 -> 名称
 * - codeToChildren: 代码 -> 下级节点（省级根使用 'ROOT' 作为父键）
 * - codeToParent: 代码 -> 父级代码（省级 parent 为 null）
 * - normalizedLabel 索引仅在运行时按需匹配，不做全量 Map 以避免额外体积
 */
const codeToLabel = new Map<string, string>()
const codeToChildren = new Map<string, ReadonlyArray<AreaOption>>()
const codeToParent = new Map<string, string | null>()

;(function buildIndexes() {
  function walk(nodes: ReadonlyArray<AreaOption>, parent: string | null) {
    for (const node of nodes) {
      codeToLabel.set(node.value as string, node.label)
      codeToParent.set(node.value as string, parent)
      if (node.children && node.children.length) {
        codeToChildren.set(node.value as string, node.children)
        walk(node.children, node.value as string)
      } else {
        codeToChildren.set(node.value as string, [])
      }
    }
  }
  // 顶层作为 ROOT 的 children，便于统一处理
  codeToChildren.set('ROOT', CHINA_AREA_OPTIONS)
  walk(CHINA_AREA_OPTIONS, null)
})()

export function findNodeByCodePath(codePath: string[]): ReadonlyArray<AreaOption> {
  const result: AreaOption[] = []
  let current: ReadonlyArray<AreaOption> = CHINA_AREA_OPTIONS
  for (const code of codePath) {
    const node = current.find(n => n.value === code)
    if (!node) break
    result.push(node)
    current = node.children || []
  }
  return result
}

export function codesToLabels(codePath: string[]): string[] {
  return findNodeByCodePath(codePath).map(n => n.label)
}

export function codesToFullName(codePath: string[], sep = ' / '): string {
  return codesToLabels(codePath).join(sep)
}

/**
 * 根据单个代码返回中文名
 */
export function codeToName(code: string): string | undefined {
  return codeToLabel.get(code)
}

/**
 * 通过末级代码反推完整代码路径（省/市/区）
 */
export function codeToPath(code: string): string[] | null {
  if (!codeToLabel.has(code)) return null
  const path: string[] = []
  let cur: string | null | undefined = code
  while (cur) {
    path.push(cur)
    cur = codeToParent.get(cur) ?? null
  }
  return path.reverse()
}

/**
 * 名称标准化：去除常见后缀与空白
 */
function normalizeName(name: string): string {
  const trimmed = name.replace(/\s+/g, '')
  const suffixes = [
    '省','市','区','县','自治县','自治州','地区','盟','旗','自治旗','特别行政区','回族自治区','维吾尔自治区','壮族自治区','市辖区'
  ]
  for (const s of suffixes) {
    if (trimmed.endsWith(s)) return trimmed.slice(0, -s.length)
  }
  return trimmed
}

/**
 * 按名称路径查找代码路径（名称可为含后缀的人类可读形式）
 * @example namesToCodes(['上海市','浦东新区']) -> ['31xxxx','3101xx','310115']
 */
export function namesToCodes(names: string[]): string[] | null {
  if (!names || !names.length) return null
  const norm = names.map(n => normalizeName(n))
  const path: string[] = []

  // 省级匹配
  const provinces = codeToChildren.get('ROOT') || []
  const p = provinces.find(p => normalizeName(p.label) === norm[0])
  if (!p) return null
  path.push(p.value as string)

  // 市级匹配
  if (norm.length >= 2) {
    const cities = codeToChildren.get(p.value as string) || []
    const c = cities.find(c => normalizeName(c.label) === norm[1])
    if (!c) return null
    path.push(c.value as string)

    // 区县级匹配
    if (norm.length >= 3) {
      const districts = codeToChildren.get(c.value as string) || []
      const d = districts.find(d => normalizeName(d.label) === norm[2])
      if (!d) return null
      path.push(d.value as string)
    }
  }

  return path
}

/**
 * 校验代码路径是否存在
 */
export function isValidCodePath(codePath: string[]): boolean {
  return findNodeByCodePath(codePath).length === codePath.length
}


